import { Card, Fade, Icon, MenuItem, Modal, Select } from "@mui/material";
import SoftBadge from "components/SoftBadge";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useQuery, gql } from "@apollo/client";
import { getUproColor } from "utils/colors";

const style = {
	position: "absolute",
	top: "50%",
	left: "50%",
	transform: "translate(-50%, -50%)",
	maxHeight: "95%",
	width: { xs: "90%", sm: "90%", xxl: "80%" },
	overflowY: "auto",
	p: 1,
};

const CALENDAR_MONTHS = [
	"Ene.",
	"Feb.",
	"Mar.",
	"Abr.",
	"May.",
	"Jun.",
	"Jul.",
	"Ago.",
	"Sept.",
	"Oct.",
	"Nov.",
	"Dic.",
];

const CALENDAR_DAYS = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];

// GraphQL query para obtener provisiones con pagos pendientes
const GET_PAGOS_PENDIENTES = gql`
  query getPagosPendientes {
    provisiones {
      id
      total
      fechaVencimiento
      estado
      pedidoCompra {
        id
        nombre
      }
      proveedor {
        id
        tipoProveedor
        razonSocial
        nombre
        apellido
      }
      ingresos {
        id
        fechaEntrega
        pagosProveedor {
          id
          estado
          pagado
          fechaEstimadaPago
          numeroFactura
        }
      }
    }
  }
`;

// Estados para las facturas
const ESTADOS_FACTURA = {
  "Pendiente": { color: getUproColor("warning"), label: "Pendiente", icon: "schedule" },
  "Aprobado": { color: getUproColor("success"), label: "Aprobado", icon: "check_circle" },
  "Rechazado": { color: getUproColor("error"), label: "Rechazado", icon: "cancel" },
  "OPEmitida": { color: getUproColor("info"), label: "OP Emitida", icon: "description" },
  "OPFinalizada": { color: getUproColor("uproGreen"), label: "OP Finalizada", icon: "check_circle_outline" },
  "SinFacturar": { color: getUproColor("uproPink"), label: "Sin facturar", icon: "receipt_long" }
};

function CalendarioPagos({
	open = false,
	handleClose = () => { }
}) {

	const [calendarMonth, setCalendarMonth] = useState(dayjs().month());
	const [calendarYear, setCalendarYear] = useState(dayjs().year());
	const [selectedDate, setSelectedDate] = useState(null);

	const today = dayjs().startOf("day");

	// Query para obtener los pagos pendientes
	const { data: dataPagos, loading: loadingPagos } = useQuery(GET_PAGOS_PENDIENTES, {
		fetchPolicy: 'cache-and-network'
	});

	const formattedSelectedDate = selectedDate
		? `${CALENDAR_DAYS[selectedDate.day()]}, ${selectedDate.date()} de ${CALENDAR_MONTHS[selectedDate.month()]} de ${selectedDate.year()}`
		: "Selecciona un día";

	const handleDaySelection = (date) => {
		setSelectedDate(date);
	};

	const handleCalendarMonthChange = (event) => {
		setCalendarMonth(Number(event.target.value));
		setSelectedDate(null);
	};

	const handleCalendarYearChange = (event) => {
		setCalendarYear(Number(event.target.value));
		setSelectedDate(null);
	};

	// Procesar los datos para crear eventos del calendario
	const calendarEvents = useMemo(() => {
		if (!dataPagos?.provisiones) return [];

		const events = [];
		
		dataPagos.provisiones.forEach(provision => {
			provision.ingresos?.forEach(ingreso => {
				const pagoProveedor = ingreso.pagosProveedor;
				
				// Filtrar según los criterios: pagado = false, estado = "OPEmitida", fechaEstimadaPago != null
				if (pagoProveedor && 
					!pagoProveedor.pagado && 
					pagoProveedor.estado === "OPEmitida" && 
					pagoProveedor.fechaEstimadaPago) {
					
					const date = dayjs(pagoProveedor.fechaEstimadaPago).startOf("day");
					const proveedorNombre = provision.proveedor?.nombre && provision.proveedor?.apellido
						? `${provision.proveedor.nombre} ${provision.proveedor.apellido}`
						: provision.proveedor?.razonSocial || 'Sin proveedor';

					events.push({
						id: `${provision.id}-${ingreso.id}`,
						date,
						dateKey: date.format("YYYY-MM-DD"),
						// Datos del remito/ingreso
						numeroRemito: ingreso.id,
						numeroOP: provision.id,
						proveedor: proveedorNombre,
						fechaEntrega: ingreso.fechaEntrega ? dayjs(ingreso.fechaEntrega).format('DD/MM/YYYY') : '-',
						estadoFactura: ESTADOS_FACTURA[pagoProveedor.estado] || ESTADOS_FACTURA["SinFacturar"],
						fechaPago: dayjs(pagoProveedor.fechaEstimadaPago).format('DD/MM/YYYY'),
						// Datos adicionales
						numeroFactura: pagoProveedor.numeroFactura || '-',
						pedidoNombre: provision.pedidoCompra?.nombre || '-',
						pedidoId: provision.pedidoCompra?.id || '-'
					});
				}
			});
		});

		return events;
	}, [dataPagos]);

	const yearOptions = useMemo(() => {
		const baseYear = dayjs().year();
		const years = Array.from({ length: 11 }, (_, index) => baseYear - 5 + index);
		if (!years.includes(calendarYear)) {
			years.push(calendarYear);
			years.sort((a, b) => a - b);
		}
		return years;
	}, [calendarYear]);

	const eventsByDate = useMemo(() => {
		const map = new Map();
		calendarEvents.forEach((event) => {
			if (!map.has(event.dateKey)) {
				map.set(event.dateKey, []);
			}
			map.get(event.dateKey).push(event);
		});
		return map;
	}, [calendarEvents]);

	// Eventos del día seleccionado
	const selectedDayEvents = useMemo(() => {
		if (!selectedDate) return [];
		const dateKey = selectedDate.format("YYYY-MM-DD");
		return eventsByDate.get(dateKey) || [];
	}, [selectedDate, eventsByDate]);

	const calendarDays = useMemo(() => {
		const monthStart = dayjs().year(calendarYear).month(calendarMonth).startOf("month");
		const firstDayOfMonth = monthStart.day();
		const daysInMonth = monthStart.daysInMonth();
		const prevMonth = monthStart.subtract(1, "month");
		const nextMonth = monthStart.add(1, "month");

		const days = [];

		for (let i = firstDayOfMonth - 1; i >= 0; i -= 1) {
			const dayNumber = prevMonth.daysInMonth() - i;
			const date = prevMonth.date(dayNumber);
			days.push({ date, current: false });
		}

		for (let day = 1; day <= daysInMonth; day += 1) {
			days.push({ date: monthStart.date(day), current: true });
		}

		const remainingCells = (7 - (days.length % 7)) % 7;
		for (let i = 1; i <= remainingCells; i += 1) {
			days.push({ date: nextMonth.date(i), current: false });
		}

		return days;
	}, [calendarMonth, calendarYear]);

	return (
		<Modal
			aria-labelledby="transition-modal-title"
			aria-describedby="transition-modal-description"
			open={open}
			onClose={handleClose}
			closeAfterTransition
		>
			<Fade in={open}>
				<Card sx={style}>
					<Card style={{
						width: "100%",
						height: "100%",
						transition: "all 0.5s",
					}}>
						<SoftBox display="flex" flexDirection="column" gap={3} p={3}>
							<SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
								<SoftBox display="flex" alignItems="center" gap={1}>
									<Icon color="primary">calendar_today</Icon>
									<SoftTypography component="span" variant="h6" fontWeight="bold">
										Calendario de pagos
									</SoftTypography>
								</SoftBox>
								<SoftBox display="flex" alignItems="center" gap={1}>
									<Select value={calendarMonth} onChange={handleCalendarMonthChange} size="small">
										{CALENDAR_MONTHS.map((monthName, index) => (
											<MenuItem key={monthName} value={index}>
												{monthName}
											</MenuItem>
										))}
									</Select>
									<Select value={calendarYear} onChange={handleCalendarYearChange} size="small">
										{yearOptions.map((yearOption) => (
											<MenuItem key={yearOption} value={yearOption}>
												{yearOption}
											</MenuItem>
										))}
									</Select>
								</SoftBox>
							</SoftBox>
							<SoftBox>
								<SoftBox display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
									{CALENDAR_DAYS.map((day) => (
										<SoftBox key={day} textAlign="center">
											<SoftBox display={{ xs: "none", sm: "block" }}>
												<SoftBadge color="uproYellow" badgeContent={day} />
											</SoftBox>
											<SoftBox display={{ xs: "block", sm: "none" }}>
												<SoftBadge color="uproYellow" badgeContent={day.slice(0, 3)} />
											</SoftBox>
										</SoftBox>
									))}
								</SoftBox>
								<SoftBox mt={2} display="grid" gridTemplateColumns="repeat(7, 1fr)" rowGap={1.5}>
									{calendarDays.map(({ date, current }, index) => {
										const dateKey = date.format("YYYY-MM-DD");
										const hasEvents = eventsByDate.has(dateKey);
										const isSelected = selectedDate && selectedDate.isSame(date, "day");
										return (
											<SoftBox key={`${dateKey}-${index}`} display="flex" justifyContent="center" alignItems="center">
												<SoftBox
													onClick={current ? () => handleDaySelection(date) : undefined}
													sx={{
														position: "relative",
														width: 44,
														height: 44,
														borderRadius: "50%",
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														cursor: current ? "pointer" : "default",
														backgroundColor: isSelected ? "#26c6da" : "transparent",
														color: !current ? "#b0bec5" : isSelected ? "#ffffff" : "#263238",
														transition: "all 0.2s ease-in-out",
														"&:hover": current ? { backgroundColor: isSelected ? "#26c6da" : "#e0f7fa" } : {},
													}}
												>
													<SoftTypography variant="body2" fontWeight="bold">
														{date.date()}
													</SoftTypography>
													{hasEvents && (
														<SoftBox
															sx={{
																width: "15px",
																height: "15px",
																borderRadius: "100%",
																position: "absolute",
																zIndex: 2,
																top: "0",
																right: "0",
																fontSize: "15px",
															}}
														>
															<Icon sx={{ fontSize: 10, color: "#e91e63" }}>fiber_manual_record</Icon>
														</SoftBox>
													)}
												</SoftBox>
											</SoftBox>
										);
									})}
								</SoftBox>
							</SoftBox>
							<SoftBox>
								<SoftTypography variant="button" fontWeight="bold">
									{formattedSelectedDate}
								</SoftTypography>
								{calendarEvents.length === 0 ? (
									<SoftBox mt={4} display="flex" alignItems="center" justifyContent="center">
										<SoftTypography component="span" variant="body2" color="text">
											{loadingPagos ? "Cargando pagos..." : "No hay pagos programados"}
										</SoftTypography>
									</SoftBox>
								) : selectedDate ? (
									selectedDayEvents.length ? (
										<SoftBox mt={2}>
											<SoftBox
												display={{ xs: "none", md: "grid" }}
												gridTemplateColumns="repeat(7, 1fr)"
												px={2}
												py={1}
												sx={{ backgroundColor: "#f0f4f8", borderRadius: 1 }}
											>
												<SoftTypography variant="caption" fontWeight="bold">N° Remito</SoftTypography>
												<SoftTypography variant="caption" fontWeight="bold">N° OP</SoftTypography>
												<SoftTypography variant="caption" fontWeight="bold">Proveedor</SoftTypography>
												<SoftTypography variant="caption" fontWeight="bold">Fecha Entrega</SoftTypography>
												<SoftTypography variant="caption" fontWeight="bold">Estado Factura</SoftTypography>
												<SoftTypography variant="caption" fontWeight="bold">Fecha Pago</SoftTypography>
												<SoftTypography variant="caption" fontWeight="bold" textAlign="center">Acciones</SoftTypography>
											</SoftBox>

											{selectedDayEvents.map((event) => (
												<SoftBox key={event.id}>
													<SoftBox
													display={{ xs: "none", md: "grid" }}
													gridTemplateColumns="repeat(7, 1fr)"
													px={2}
													py={1.5}
													sx={{ borderBottom: "1px solid #eceff1", alignItems: "center" }}
												>
													<SoftTypography variant="caption" fontWeight="bold">
														{event.numeroRemito}
													</SoftTypography>
													<SoftTypography variant="caption" fontWeight="bold">
														{event.numeroOP}
													</SoftTypography>
													<SoftTypography variant="caption">
														{event.proveedor}
													</SoftTypography>
													<SoftTypography variant="caption">
														{event.fechaEntrega}
													</SoftTypography>
													<SoftBox display="flex" alignItems="center" gap={1}>
														<Icon sx={{ color: event.estadoFactura.color || getUproColor("info"), fontSize: "small" }}>
															{event.estadoFactura.icon}
														</Icon>
														<SoftTypography variant="caption">
															{event.estadoFactura.label}
														</SoftTypography>
													</SoftBox>
													<SoftTypography variant="caption" fontWeight="medium" color="info">
														{event.fechaPago}
													</SoftTypography>
													<SoftBox display="flex" justifyContent="center" gap={1}>
														<SoftButton
															variant="text"
															color="primary"
															size="small"
															iconOnly
															circular
															onClick={() => {
																if (typeof window !== "undefined") {
																	window.open(`/remitoIndividual/pdf/${event.numeroRemito}`, "_blank", "noopener,noreferrer");
																}
															}}
															title="Ver Remito"
														>
															<Icon sx={{ fontSize: 18 }}>picture_as_pdf</Icon>
														</SoftButton>
														<SoftButton
															variant="text"
															color="uproPink"
															size="small"
															iconOnly
															circular
															onClick={() => {
																if (typeof window !== "undefined") {
																	window.open(`/orden-pago/pdf/${event.numeroRemito}`, "_blank", "noopener,noreferrer");
																}
															}}
															title="Ver Orden de Pago"
														>
															<Icon sx={{ fontSize: 18 }}>paid</Icon>
														</SoftButton>
													</SoftBox>
												</SoftBox>													<SoftBox
														display={{ xs: "block", md: "none" }}
														sx={{
															border: "1px solid #eceff1",
															borderRadius: 1,
															mb: 2,
															p: 2,
															backgroundColor: "#fafafa",
														}}
													>
														<SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
													<SoftBox display="flex" alignItems="center" gap={1}>
														<Icon sx={{ color: event.estadoFactura.color || getUproColor("info") }}>{event.estadoFactura.icon}</Icon>
														<SoftTypography variant="body2" fontWeight="bold">
															{event.estadoFactura.label}
														</SoftTypography>
													</SoftBox>
													<SoftBox display="flex" gap={1}>
														<SoftButton
															variant="text"
															color="primary"
															size="small"
															iconOnly
															circular
															onClick={() => {
																if (typeof window !== "undefined") {
																	window.open(`/remitoIndividual/pdf/${event.numeroRemito}`, "_blank", "noopener,noreferrer");
																}
															}}
															title="Ver Remito"
														>
															<Icon sx={{ fontSize: 16 }}>picture_as_pdf</Icon>
														</SoftButton>
														<SoftButton
															variant="text"
															color="uproPink"
															size="small"
															iconOnly
															circular
															onClick={() => {
																if (typeof window !== "undefined") {
																	window.open(`/orden-pago/pdf/${event.numeroRemito}`, "_blank", "noopener,noreferrer");
																}
															}}
															title="Ver Orden de Pago"
														>
															<Icon sx={{ fontSize: 16 }}>paid</Icon>
														</SoftButton>
													</SoftBox>
												</SoftBox>														<SoftBox mb={1}>
															<SoftTypography variant="caption" color="text" fontWeight="bold">
																N° Remito: {" "}
															</SoftTypography>
															<SoftTypography variant="caption" fontWeight="bold">{event.numeroRemito}</SoftTypography>
														</SoftBox>

														<SoftBox mb={1}>
															<SoftTypography variant="caption" color="text" fontWeight="bold">
																N° OP: {" "}
															</SoftTypography>
															<SoftTypography variant="caption" fontWeight="bold">{event.numeroOP}</SoftTypography>
														</SoftBox>

														<SoftBox mb={1}>
															<SoftTypography variant="caption" color="text" fontWeight="bold">
																Proveedor: {" "}
															</SoftTypography>
															<SoftTypography variant="caption">{event.proveedor}</SoftTypography>
														</SoftBox>

														<SoftBox mb={1}>
															<SoftTypography variant="caption" color="text" fontWeight="bold">
																Fecha de Entrega: {" "}
															</SoftTypography>
															<SoftTypography variant="caption">{event.fechaEntrega}</SoftTypography>
														</SoftBox>

														<SoftBox>
															<SoftTypography variant="caption" color="text" fontWeight="bold">
																Fecha de Pago: {" "}
															</SoftTypography>
															<SoftTypography variant="caption" color="info" fontWeight="medium">
																{event.fechaPago}
															</SoftTypography>
														</SoftBox>
													</SoftBox>
												</SoftBox>
											))}
										</SoftBox>
									) : (
										<SoftBox mt={4} display="flex" alignItems="center" justifyContent="center">
											<SoftTypography component="span" variant="body2" color="text">
												No hay pagos programados para este día
											</SoftTypography>
										</SoftBox>
									)
								) : (
									<SoftBox mt={4} display="flex" alignItems="center" justifyContent="center">
										<SoftTypography component="span" variant="body2" color="text">
											Selecciona un día para ver los pagos programados
										</SoftTypography>
									</SoftBox>
								)}
							</SoftBox>
						</SoftBox>
					</Card>
				</Card>
			</Fade>
		</Modal>
	)
}

export default CalendarioPagos
